import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getFaq, deleteFaq } from "../../../../services/packageService";
import Toster from "../../../common/Toster";
import {
  pbAddBtn,
  pbDeleteBtn,
  pbFieldInput,
  pbIndexBadge,
  pbItemCard,
  pbSectionTitle,
} from "../packageBuilderUi";

const Faq = ({ setFAQData, packageid, onUserEdit }) => {
  const queryClient = useQueryClient();
  const [localFaqs, setLocalFaqs] = useState([{ question: "", answer: "" }]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { data: faqData } = useQuery({
    queryKey: ["faq", packageid],
    queryFn: () => getFaq(packageid),
    enabled: !!packageid,
  });

  const deleteFaqMutation = useMutation({
    mutationFn: deleteFaq,
    onSuccess: () => {
      queryClient.invalidateQueries(["faq", packageid]);
      setShowSuccess(true);
      setSuccessMessage("FAQ deleted successfully");
    },
    onError: (error) => {
      setShowError(true);
      setError(error.message || "Failed to delete FAQ");
    },
  });

  useEffect(() => {
    if (faqData?.data) {
      const formattedFaqs = faqData.data.map((faq) => ({
        id: faq.id,
        question: faq.question,
        answer: faq.answer,
      }));
      setLocalFaqs(
        formattedFaqs.length > 0
          ? formattedFaqs
          : [{ question: "", answer: "" }]
      );
    }
  }, [faqData]);

  useEffect(() => {
    if (setFAQData) {
      const formattedData = {
        package_id: packageid,
        is_updated: faqData?.data?.length > 0 ? 1 : 0,
        data: localFaqs.map((faq) => ({
          question: faq.question,
          answer: faq.answer,
          is_active: true,
        })),
      };
      setFAQData(formattedData);
    }
  }, [localFaqs, setFAQData, packageid, faqData?.data?.length]);

  const handleQuestionChange = (index, value) => {
    onUserEdit?.();
    const newFaqs = [...localFaqs];
    newFaqs[index] = {
      ...newFaqs[index],
      question: value,
    };
    setLocalFaqs(newFaqs);
  };

  const handleAnswerChange = (index, value) => {
    onUserEdit?.();
    const newFaqs = [...localFaqs];
    newFaqs[index] = {
      ...newFaqs[index],
      answer: value,
    };
    setLocalFaqs(newFaqs);
  };

  const addFAQ = () => {
    onUserEdit?.();
    setLocalFaqs([...localFaqs, { question: "", answer: "" }]);
  };

  const removeFAQ = async (index) => {
    onUserEdit?.();
    const faq = localFaqs[index];
    if (faq.id) {
      try {
        await deleteFaqMutation.mutateAsync(faq.id);
      } catch (err) {
        console.error("Delete failed:", err);
        return;
      }
    }
    const newFaqs = localFaqs.filter((_, i) => i !== index);
    setLocalFaqs(newFaqs.length > 0 ? newFaqs : [{ question: "", answer: "" }]);
  };

  return (
    <>
      {showError && error && (
        <Toster
          message={error}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => setShowSuccess(false)}
        />
      )}

      <div className="space-y-4">
        {localFaqs.map((faq, index) => (
          <div key={index} className={pbItemCard}>
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className={pbIndexBadge}>{index + 1}</span>
                <h3 className={pbSectionTitle}>FAQ entry</h3>
              </div>
              {localFaqs.length > 1 && (
                <button
                  onClick={() => removeFAQ(index)}
                  className={pbDeleteBtn}
                  title="Remove FAQ"
                >
                  <i className="fi fi-rr-trash text-xs"></i>
                </button>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Question <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) =>
                    handleQuestionChange(index, e.target.value)
                  }
                  className={pbFieldInput}
                  placeholder="Enter question..."
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">
                  Answer <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={faq.answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  className={`${pbFieldInput} min-h-[88px] resize-y`}
                  rows={3}
                  placeholder="Enter answer..."
                />
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={addFAQ}
          className={pbAddBtn}
        >
          <i className="fi fi-rr-plus text-xs"></i>
          Add FAQ
        </button>
      </div>
    </>
  );
};

export default Faq;
